/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';

import useMergedRef from 'hooks/useMergedRef';
import useUpdateEffect from 'hooks/useUpdateEffect';

import Image from './Image';
import Popper from './Popper';
import * as S from './styled/Tooltip.styled';

/**
 * @author Burak Unsal
 * @param {Object} props
 * @param {string} [props.title] - The title of the tooltip.
 * @param {string} [props.header] - The header of the tooltip.
 * @param {string} [props.image] - The image of the tooltip.
 * @param {string} [props.ctaButtonLabel] - The cta button label of the tooltip.
 * @param {function} [props.ctaButtonOnClick] - The cta button onClick of the tooltip.
 * @param {string} [props.placement = 'right'] - The placement of the tooltip.
 * @param {string} [props.color = 'var(--background-4)'] - The color of the tooltip.
 * @param {React.RefObject} [props.componentRef] - The reference of the component.
 * @param {React.RefObject} [props.scrollRef] - The reference of the scrollable component.
 * @param {number} [props.timeout] - The timeout of the tooltip.
 * @param {Object} [props.containerCss] - The style of the tooltip container.
 * @param {Object} [props.popperContainerCss] - The style of the tooltip popper container.
 * @param {boolean} [props.showOnMobile = true] - The showOnMobile of the tooltip.
 * @param {React.ReactNode} [props.children] - The children of the tooltip.
 * @returns {JSX.Element}
 * @example
 * <Tooltip title="testTooltip" placement="right" color="var(--background-4)" scrollRef={scrollRef} timeout={1000}>
 * 	<span>test</span>
 * </Tooltip>
 * @example
 * <Tooltip title="testTooltip" placement="right" color="var(--background-4)" componentRef={ref} scrollRef={scrollRef} timeout={1000} />
 */
const Tooltip = (props) => {
	const { componentRef } = props;

	const [componentRefState, setComponentRefState] = useState(componentRef);

	useUpdateEffect(() => {
		setComponentRefState(componentRef);
	}, [componentRef]);

	return componentRefState !== undefined ? (
		<TooltipWithComponentRef {...{ ...props, componentRef: componentRefState }} />
	) : (
		<TooltipWithChildrenRef {...props} />
	);
};

const TooltipWithComponentRef = (props) => <TooltipCore {...props} />;

const TooltipWithChildrenRef = (props) => {
	const { children } = props;

	const [tooltipRef, setTooltipRef] = useState(null);
	const refs = useMergedRef(children?.ref, setTooltipRef);
	const [childrenProps, setChildrenProps] = useState({
		...children.props,
		ref: refs
	});

	useUpdateEffect(() => {
		setChildrenProps({
			...children.props,
			ref: refs
		});
	}, [refs, children.props]);

	return (
		<>
			{React.cloneElement(children, childrenProps)}
			<TooltipCore
				{...{
					...props,
					componentRef: tooltipRef
				}}
			/>
		</>
	);
};

const TooltipCore = (props) => {
	const {
		title,
		placement = 'right',
		color = 'var(--tooltipBackgroundColor)',
		componentRef,
		scrollRef,
		timeout = 300,
		containerCss,
		popperContainerCss,
		showOnMobile = true,
		offsetX,
		offsetY,
		header,
		image,
		ctaButtonLabel,
		ctaButtonOnClick
	} = props;

	const { t } = useTranslation();

	const stopMouseEvents = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	return (
		<>
			{title && componentRef && (
				<Popper
					refElement={componentRef}
					placement={placement}
					wrapperId="Tooltip"
					containerClassName="tooltip"
					arrowColor={color}
					scrollRef={scrollRef}
					hoverMode
					timeout={timeout}
					containerCss={popperContainerCss}
					offsetX={offsetX}
					offsetY={offsetY}
					showOnMobile={showOnMobile}
					showArrow
					disableMobileView>
					{(closePopper, updatePosition) => (
						<S.Container
							className="tooltip-container"
							color={color}
							css={containerCss}
							onContextMenu={stopMouseEvents}
							onMouseUp={stopMouseEvents}
							onClick={stopMouseEvents}>
							{image && <Image url={image} width="100%" height="100%" />}
							{header && <span className="tooltip-header">{t(header)}</span>}
							{typeof title === 'string' ? (
								<span className="tooltip-text">{t(title)}</span>
							) : typeof title === 'function' ? (
								title(closePopper, updatePosition)
							) : (
								title
							)}
							{ctaButtonLabel && (
								<Button
									label={ctaButtonLabel}
									onClick={ctaButtonOnClick}
									className="tooltip-cta-button"
								/>
							)}
						</S.Container>
					)}
				</Popper>
			)}
		</>
	);
};

export default Tooltip;
